import api from '@/lib/api';
import type { Asset, CreateAssetInput, UpdateAssetInput, InventoryEmployee } from '@hygge/shared';
import { CACHE_TIME } from '@hygge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { assetTypesKeys } from './use-asset-types';

export const assetsKeys = {
  all: ['assets'] as const,
  lists: () => [...assetsKeys.all, 'list'] as const,
  byEmployee: (employeeId: string) => [...assetsKeys.lists(), { employeeId }] as const,
};

export const inventoryKeys = {
  all: ['inventory'] as const,
  list: (params?: Record<string, string>) => [...inventoryKeys.all, 'list', params] as const,
};

async function getEmployeeAssets(employeeId: string): Promise<Asset[]> {
  const { data } = await api.get<Asset[]>(`/assets/employee/${employeeId}`);
  return data;
}

async function createAsset(input: CreateAssetInput): Promise<Asset> {
  const { data } = await api.post<Asset>('/assets', input);
  return data;
}

async function updateAsset(id: string, input: UpdateAssetInput): Promise<Asset> {
  const { data } = await api.put<Asset>(`/assets/${id}`, input);
  return data;
}

async function deleteAsset(id: string): Promise<void> {
  await api.delete(`/assets/${id}`);
}

async function markAssetReturned({ id, returnedAt }: { id: string; returnedAt?: string }): Promise<Asset> {
  const { data } = await api.patch<Asset>(`/assets/${id}/return`, { returnedAt });
  return data;
}

async function getInventory(params?: Record<string, string>): Promise<InventoryEmployee[]> {
  const { data } = await api.get<InventoryEmployee[]>('/assets/inventory', { params });
  return data;
}

export function useEmployeeAssets(employeeId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: assetsKeys.byEmployee(employeeId),
    queryFn: () => getEmployeeAssets(employeeId),
    enabled: !!employeeId && (options?.enabled ?? true),
    staleTime: CACHE_TIME.SHORT,
  });
}

// Backs both the Inventory create dialog (has its own inline error) and the
// employee-profile Assets tab (no handling of its own) — takes an opt-in
// flag instead of a fixed meta so each caller gets the right behavior.
export function useCreateAsset(opts?: { suppressGlobalToast?: boolean }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAsset,
    meta: { suppressGlobalToast: opts?.suppressGlobalToast ?? false },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: assetsKeys.byEmployee(variables.employeeId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      queryClient.invalidateQueries({ queryKey: assetTypesKeys.all });
    },
  });
}

export function useUpdateAsset(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateAssetInput & { id: string }) => updateAsset(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetsKeys.byEmployee(employeeId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    },
  });
}

export function useDeleteAsset(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAsset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetsKeys.byEmployee(employeeId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      queryClient.invalidateQueries({ queryKey: assetTypesKeys.all });
    },
  });
}

export function useMarkAssetReturned(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAssetReturned,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetsKeys.byEmployee(employeeId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    },
  });
}

export function useInventoryDeleteAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAsset,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      queryClient.invalidateQueries({ queryKey: assetsKeys.all });
      queryClient.invalidateQueries({ queryKey: assetTypesKeys.all });
    },
  });
}

export function useInventoryReturnAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAssetReturned,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      queryClient.invalidateQueries({ queryKey: assetsKeys.all });
    },
  });
}

export function useInventory(params?: Record<string, string>) {
  return useQuery({
    queryKey: inventoryKeys.list(params),
    queryFn: () => getInventory(params),
    staleTime: CACHE_TIME.DEFAULT,
  });
}
